import { useEffect, useState, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { onAuthStateChanged, User } from "firebase/auth";
import { auth, functions } from "../firebase";
import { TEMPLATES, FIND_SOMEONE_WHO } from "../lib/templates";

export default function TeacherCreate() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [className, setClassName] = useState("");
  const [templateId, setTemplateId] = useState(FIND_SOMEONE_WHO.templateId);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      if (!u || u.isAnonymous) navigate("/teacher");
      else setUser(u);
    });
  }, [navigate]);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setError(null);
    setCreating(true);
    try {
      const tmpl = TEMPLATES.find((t) => t.templateId === templateId);
      if (!tmpl) throw new Error("Template not found.");

      // The class and its hunt are created server-side in one batch. The old
      // client path probed `edu_classes where joinCode == candidate` for
      // uniqueness, which the scoped rules deny (a teacher cannot prove a
      // stranger's class matches their uid) and which leaked an existence
      // oracle for join codes. It also wrote the two docs unatomically, so a
      // failure between them orphaned a class.
      const create = httpsCallable<
        {
          className: string;
          template: {
            templateId: string;
            title: string;
            durationMinutes: number;
            challenges: { text: string; hint: string | null; points: number }[];
          };
        },
        { classId: string; huntId: string; joinCode: string }
      >(functions, "createEduClass");

      await create({
        className: className.trim(),
        template: {
          templateId: tmpl.templateId,
          title: tmpl.title,
          durationMinutes: tmpl.durationMinutes,
          challenges: tmpl.challenges,
        },
      });

      navigate("/teacher/dashboard");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to create.";
      setError(msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <main className="screen">
      <div className="card wide">
        <h1>New hunt</h1>
        <form onSubmit={create}>
          <label>
            Class name
            <input
              value={className}
              onChange={(e) => setClassName(e.target.value)}
              placeholder="e.g. Period 3 Math"
            />
          </label>

          <div>
            <span className="label-row">Choose a template</span>
            <ul className="template-grid">
              {TEMPLATES.map((t) => {
                const selected = t.templateId === templateId;
                return (
                  <li key={t.templateId}>
                    <button
                      type="button"
                      className={`template-card${selected ? " selected" : ""}`}
                      onClick={() => setTemplateId(t.templateId)}
                      aria-pressed={selected}
                    >
                      <span className="template-emoji" aria-hidden>{t.emoji}</span>
                      <span className="template-title">{t.title}</span>
                      <span className="template-desc">{t.description}</span>
                      <span className="template-meta">
                        {t.challenges.length} challenges · {t.subject} · {t.gradeRange}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {error && <p className="error">{error}</p>}
          <button type="submit" disabled={creating}>
            {creating ? "Creating..." : "Create class + hunt"}
          </button>
        </form>
      </div>
    </main>
  );
}
